/**
 * ClubDetailsPage Component
 * Main page for the Club Details Card Grid Layout
 * 
 * Features preserved from original ClubDetails.js:
 * - All action button states based on membership status
 * - Program management (add, edit, delete) for managers
 * - Category management
 * - Search and filters
 * - Capacity indicators
 * - Follow functionality
 * - Registration flow
 * - Join club modal
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { useUser } from '../../context/UserContext';

// Hooks
import { useClubData, useClubPermissions, useProgramFilters } from './hooks';
import { getProgramCountsByCategory } from './utils/programHelpers';

// Components
import ClubHeader from './components/ClubHeader';
import FilterBar from './components/FilterBar';
import ProgramGrid from './components/ProgramGrid';

// Existing shared components/modals
import ProgramWizard from '../../components/ProgramWizard';
import CategoryManagementModal from '../../components/Club/CategoryManagementModal';
import JoinClubModal from '../../components/Club/JoinClubModal';

import styles from './ClubDetailsPage.module.css';

const ClubDetailsPage = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUserClubs } = useUser();

  // ═══════════════════════════════════════════════════
  // DATA HOOKS
  // ═══════════════════════════════════════════════════
  const {
    clubDetails,
    programs,
    categories,
    isInitialLoading,
    programsLoading,
    hasError,
    refetch,
    deleteProgram
  } = useClubData(clubId);

  const { canManage, isClubManager, userClubRole } = useClubPermissions(clubId, clubDetails);

  const {
    filteredPrograms,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    showFilters,
    toggleFilters
  } = useProgramFilters(programs);

  // ═══════════════════════════════════════════════════
  // LOCAL STATE
  // ═══════════════════════════════════════════════════
  
  // Modal states
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showJoinClubModal, setShowJoinClubModal] = useState(false);

  // ═══════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════
  
  // Handle navigation state (e.g., deep link to notifications)
  useEffect(() => {
    if (location.state?.showNotifications) {
      // Could handle opening specific section
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  // ═══════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════

  // Program selection - Navigate to full program page
  const handleProgramSelect = (program) => {
    navigate(`/clubs/${clubId}/programs/${program._id}`);
  };

  // Program management
  const handleAddProgram = () => {
    setEditingProgram(null);
    setShowProgramForm(true);
  };

  const handleEditProgram = (program) => {
    setEditingProgram(program);
    setShowProgramForm(true);
  };

  const handleDeleteProgram = async (programId) => {
    if (window.confirm('Are you sure you want to delete this program? This action cannot be undone.')) {
      try {
        await deleteProgram(programId);
      } catch (err) {
        console.error('Error deleting program:', err);
        alert('Failed to delete program. Please try again.');
      }
    }
  };

  const handleManageProgram = (program) => {
    window.open(`/clubs/${clubId}/programs/${program._id}/manage`, '_blank');
  };

  const handleCloseProgramForm = () => {
    setShowProgramForm(false);
    setEditingProgram(null);
  };

  const handleFormSubmit = async () => {
    try {
      handleCloseProgramForm();
      // Refresh programs list
      await refetch.programs();
    } catch (err) {
      console.error('Error refreshing programs:', err);
      alert('Program saved but failed to refresh the list. Please refresh the page.');
    }
  };

  // Category management
  const handleCategoriesUpdated = () => {
    refetch.categories();
  };

  // Join club
  const handleJoinClubRequired = () => {
    setShowJoinClubModal(true);
  };

  const handleJoinSuccess = () => {
    setShowJoinClubModal(false);
    refreshUserClubs();
    refetch.club();
  };

  // Club management
  const handleManageClubClick = () => {
    navigate(`/club/${clubId}/edit`);
  };

  const handleNotificationsClick = () => {
    navigate(`/club/${clubId}/notifications`);
  };

  // ═══════════════════════════════════════════════════
  // COMPUTED VALUES
  // ═══════════════════════════════════════════════════
  const programCounts = getProgramCountsByCategory(programs, categories);
  
  const programCountsForHeader = {
    total: programs.length,
    byCategory: categories.reduce((acc, cat) => {
      if (cat._id !== 'all') {
        acc[cat.name] = programCounts[cat._id] || 0;
      }
      return acc;
    }, {})
  };

  // ═══════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════
  if (isInitialLoading) {
    return (
      <div className={styles.loadingContainer}>
        <ClipLoader color="#A4EB3F" loading={true} size={50} />
        <p>Loading club details...</p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // ERROR STATE
  // ═══════════════════════════════════════════════════
  if (hasError || !clubDetails) {
    return (
      <div className={styles.errorContainer}>
        <p>Sorry, we couldn't load the club details. Please try again later.</p>
        <button 
          className={styles.retryButton}
          onClick={() => refetch.all()}
        >
          Try Again
        </button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════
  
  // Apply club's primary color as CSS variable for theming
  const clubColor = clubDetails?.primaryColor || '#A4EB3F';
  const pageStyle = {
    '--club-color': clubColor,
  };

  return (
    <div className={styles.page} style={pageStyle}>
      {/* Club Header */}
      <ClubHeader
        club={clubDetails}
        programCounts={programCountsForHeader}
        canManage={canManage}
        onManageClick={handleManageClubClick}
        onNotificationsClick={handleNotificationsClick}
      />

      {/* Filter Bar */}
      <FilterBar
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filters={filters}
        onFiltersChange={setFilters}
        programCounts={programCounts}
        canManage={canManage}
        onAddProgram={handleAddProgram}
        onManageCategories={() => setShowCategoryModal(true)}
        showFilters={showFilters}
        onToggleFilters={toggleFilters}
      />

      {/* Program Grid */}
      <ProgramGrid
        programs={filteredPrograms}
        onProgramSelect={handleProgramSelect}
        clubId={clubId}
        canManage={canManage}
        isClubManager={isClubManager}
        userClubRole={userClubRole}
        isLoading={programsLoading}
        onEditProgram={handleEditProgram}
        onDeleteProgram={handleDeleteProgram}
        onManageProgram={handleManageProgram}
        onAddProgram={handleAddProgram}
        onJoinClubRequired={handleJoinClubRequired}
      />

      {/* ═══════════════════════════════════════════════════
          MODALS
          ═══════════════════════════════════════════════════ */}
      
      {/* Program Wizard Modal */}
      {showProgramForm && (
        <ProgramWizard
          isOpen={showProgramForm}
          onClose={handleCloseProgramForm}
          clubId={clubId}
          initialData={editingProgram}
          onSubmit={handleFormSubmit}
        />
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <CategoryManagementModal
          isOpen={showCategoryModal}
          onClose={() => setShowCategoryModal(false)}
          clubId={clubId}
          onCategoriesUpdated={handleCategoriesUpdated}
        />
      )}

      {/* Join Club Modal */}
      {showJoinClubModal && clubDetails && (
        <JoinClubModal
          club={clubDetails}
          onClose={() => setShowJoinClubModal(false)}
          onJoinSuccess={handleJoinSuccess}
          user={user}
        />
      )}
    </div>
  );
};

export default ClubDetailsPage;









